import React from 'react';
import CloseIcon from '@mui/icons-material/Close';
import WhatshotIcon from '@mui/icons-material/Whatshot';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import './SimulationInfoOverlay.css';
 
const fmt = (m2) => {
  if (!m2) return '—';
  if (m2 >= 1_000_000) return `${(m2 / 1_000_000).toFixed(2)} km²`;
  if (m2 >= 10_000) return `${(m2 / 10_000).toFixed(1)} ha`;
  return `${Math.round(m2)} m²`;
};
 
// ── date_first_outline : date du premier contour du feu (polygone `main`) ──
const fmtDate = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d)) return null;
  return d.toLocaleString('fr-FR', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};
 
const SimulationInfoOverlay = ({
  simulation,
  onClose,
  activeStep,   // -1 = état initial (main + start), 0..N-1 = prédictions
  totalSteps,   // nombre de prédictions uniquement (main/start non comptés)
  onStepChange,
}) => {
  if (!simulation) return null;
 
  const { fire, polygons = [] } = simulation;
 
  // ── Polygones de prédiction, triés par `order` croissant — sans ce tri,
  // predPolygons[activeStep] ne correspondait pas forcément au bon calque
  // affiché sur la carte (l'ordre du backend n'est pas garanti). ──
  const predPolygons = polygons
    .filter((p) => p.type === 'predictions')
    .sort((a, b) => Number(a.order) - Number(b.order));
 
  // activeStep = -1 → aucun polygone de prédiction actif (état initial).
  const currentPoly = activeStep === -1 ? null : (predPolygons[activeStep] ?? null);
 
  const firstOutlineDate = fmtDate(fire.date_first_outline);
 
  return (
<div className="sio-root">
      {/* Header */}
<div className="sio-header">
<WhatshotIcon
          className="sio-flame"
          style={{ fontSize: 16 }}
        />
 
        <span className="sio-title">
          {fire.reference?.trim() || `Simulation #${fire.id}`}
</span>
 
        <button
          className="sio-close"
          onClick={onClose}
>
<CloseIcon style={{ fontSize: 13 }} />
</button>
</div>
 
      {/* Premier contour (date_first_outline) */}
      {firstOutlineDate && (
<div className="sio-subtitle">
<span className="sio-subtitle-label">Premier contour</span>
<span className="sio-subtitle-value">{firstOutlineDate}</span>
</div>
      )}
 
      {/* Métriques */}
<div className="sio-metrics">
<div className="sio-metric">
<span className="sio-metric-label">
            Surface finale
</span>
<span className="sio-metric-value">
            {fmt(
              predPolygons[predPolygons.length - 1]?.area_m2
            )}
</span>
</div>
 
        <div className="sio-metric">
<span className="sio-metric-label">
            Durée
</span>
<span className="sio-metric-value">
            {fire.nb_of_hours}h
</span>
</div>
 
        <div className="sio-metric">
<span className="sio-metric-label">
            Polygones
</span>
<span className="sio-metric-value">
            {fire.nb_of_polygons}
</span>
</div>
</div>
 
      {/* Timeline */}
      {totalSteps > 0 && (
<div className="sio-timeline">
<button
            className="sio-nav-btn"
            onClick={() =>
              onStepChange(Math.max(-1, activeStep - 1))
            }
            disabled={activeStep === -1}
>
<NavigateBeforeIcon
              style={{ fontSize: 14 }}
            />
</button>
 
          <input
            type="range"
            className="sio-timeline-slider"
            min={-1}
            max={totalSteps - 1}
            step={1}
            value={activeStep}
            onChange={(e) =>
              onStepChange(Number(e.target.value))
            }
          />
 
          <span className="sio-timeline-counter">
            {activeStep === -1 ? 'Départ' : `${activeStep + 1} / ${totalSteps}`}
</span>
 
          <button
            className="sio-nav-btn"
            onClick={() =>
              onStepChange(
                Math.min(
                  totalSteps - 1,
                  activeStep + 1
                )
              )
            }
            disabled={activeStep === totalSteps - 1}
>
<NavigateNextIcon
              style={{ fontSize: 14 }}
            />
</button>
</div>
      )}
 
      {/* Infos étape */}
      {activeStep === -1 ? (
<div className="sio-step-info">
<span className="sio-step-label">Point de départ</span>
</div>
      ) : currentPoly && (
<div className="sio-step-info">
<span className="sio-step-label">
            T+{(activeStep + 1) * (fire.dt_polygons ?? 60)}min
</span>
<span className="sio-step-area">{fmt(currentPoly.area_m2)}</span>
</div>
      )}
</div>
  );
};
 
export default SimulationInfoOverlay;